import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { useAppGlobalData } from "../../hooks/useAppGlobalData";
import { useLanguage } from "../../LanguageProvider";
import { useAuth } from "../../AuthProvider";
import { PageLoader } from "../common/LoadingStates";
import { Menu, Users, Calendar, AlertCircle } from "lucide-react";

export const AppLayout = () => {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const { allParticipants, sessions, allUnpaidUsers, overdueUnpaidUsers, refreshGlobalData, refreshUnpaidUsers, loading } = useAppGlobalData();
  const location = useLocation();

  // Dark mode: respect prefers-color-scheme as default, allow manual override
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "auto") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", theme);
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => {
      if (prev === "auto") {
        // Check what the system prefers, then toggle to the opposite
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        return prefersDark ? "light" : "dark";
      }
      return prev === "dark" ? "light" : "dark";
    });
  };

  // Determine effective theme for icon display
  const isDark = theme === "dark" ||
    (theme === "auto" && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  return (
    <PageLoader loading={loading}>
      <div className="app-layout">
      {/* Mobile Sidebar Overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen && window.innerWidth <= 768 ? "visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sessions={sessions}
        allParticipants={allParticipants}
        allUnpaidUsers={allUnpaidUsers}
        overdueUnpaidUsers={overdueUnpaidUsers}
        onDataRefresh={refreshGlobalData}
        isDark={isDark}
        toggleTheme={toggleTheme}
      />

      <main className={`main-layout ${sidebarOpen && window.innerWidth > 768 ? "sidebar-expanded" : "sidebar-collapsed"}`}>
        <div className="main-content">
          <Outlet context={{
            allParticipants,
            sessions,
            allUnpaidUsers,
            refreshGlobalData,
            refreshUnpaidUsers,
            user,
            isAuthenticated
          }} />
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className="bottom-nav-mobile">
          <NavLink
            to="/"
            className={({ isActive }) => `bottom-nav-item ${isActive || location.pathname.startsWith('/sessions') ? "active" : ""}`}
            onClick={() => {
              if (window.innerWidth <= 768) setSidebarOpen(false);
            }}
          >
            <Calendar size={22} />
            <span>{t("sessions_list")}</span>
          </NavLink>

          <NavLink to="/users" className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}>
            <Users size={22} />
            <span>{t("players")}</span>
          </NavLink>

          <NavLink
            to="/debts"
            className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""} ${overdueUnpaidUsers?.length > 0 ? "has-debt" : ""}`}
          >
            <span className="bottom-nav-icon-wrap">
              <AlertCircle size={22} />
              {overdueUnpaidUsers?.length > 0 && (
                <span className="bottom-nav-badge">{overdueUnpaidUsers.length}</span>
              )}
            </span>
            <span>{t("bad_debt")}</span>
          </NavLink>

          <button
            className={`bottom-nav-item ${sidebarOpen ? "active" : ""}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu size={22} />
            <span>{t("menu")}</span>
          </button>
        </nav>
      </main>
    </div>
    </PageLoader>
  );
};
